// Trammed simulation: pure game logic, no DOM. Runs on the server and, for solo play, in the browser.
'use strict';
import { PLACES } from './places.js';

// ---------- constants ----------
// the world grows with the player count, so every city gets about as much room as in a 6-city match;
// W, H, GW and GH follow whichever map is current (setState and genMap set them)
export let W = 1600, H = 1000;
// a third more world than land needs, so there is always sea for a coastline to take its shape in
export const SEA_ROOM = 4 / 3;
export const worldSize = np => { const k = Math.sqrt(Math.max(1, np / 6) * SEA_ROOM); return { w: Math.round(1600 * k / 10) * 10, h: Math.round(1000 * k / 10) * 10 }; };
export function setDims(w, h) { W = w; H = h; GW = w / CELL; GH = h / CELL; }
export const SHAPES = ['triangle', 'circle', 'square', 'pentagon', 'diamond', 'star', 'hexagon', 'cross', 'heart', 'crescent'];
export const NAMES = { triangle: 'Triangle', circle: 'Circle', square: 'Square', pentagon: 'Pentagon', diamond: 'Diamond', star: 'Star', hexagon: 'Hexagon', cross: 'Cross', heart: 'Heart', crescent: 'Crescent' };
export const MAX_PLAYERS = 30;
// past ten cities the shapes come round again; the colour tells two triangles apart
export const shapeOf = id => SHAPES[id % SHAPES.length];
const BASE_COL = ['#d9643f', '#2f7fa8', '#4e9a5f', '#d4a12a', '#8b62b0', '#cf5f86', '#2e9c94', '#8a6a3c', '#c2404f', '#5d6f9e'];
const tint = (hex, k) => { const n = parseInt(hex.slice(1), 16), f = c => Math.round(k > 0 ? c + (255 - c) * k : c * (1 + k)); return '#' + [n >> 16, n >> 8 & 255, n & 255].map(c => f(c).toString(16).padStart(2, '0')).join(''); };
export const PCOL = Array.from({ length: MAX_PLAYERS }, (_, i) => (i < 10 ? BASE_COL[i] : tint(BASE_COL[i % 10], i < 20 ? 0.38 : -0.32)));
// default names: Triangle, then Triangle II, Triangle III for the repeats
export const defaultName = id => NAMES[shapeOf(id)] + (id >= SHAPES.length ? ' ' + ['II', 'III'][Math.floor(id / SHAPES.length) - 1] : '');
export const LINE_COLORS = ['#e4572e', '#2f7fb5', '#f0a030', '#4f9d5d', '#8a63b8', '#d65d8e', '#2a9d8f'];
export const CFG = {
  trainSpeed: 115, trainCap: 6, carSeats: 3, trainCapMax: 15, carCost: 15, flightSpeed: 150, prodInterval: 3.2, grace: 8,
  overTime: 35, depotBase: 15, depotStep: 5, trainCost: 12, lineCost: 30, lineMax: 6,
  startCoins: 45, startLines: 3, upgradeBase: 25, rateCap: 10,
  cap: { factory: 10, gate: 10, depot: 8, recycler: 8 }, toll: 0.75, rushAt: 360, warehouseCost: 40, customsCost: 50, recyclerExtra: 20, annexCost: 100, salvage: 40, surgeBonus: 1.25, surgeGrace: 7, annexGrace: 15, surgeMin: 5, surgeCooldown: 30, bounty: 50,
};

// ---------- match modifiers ----------
// each bends a few rules for a whole match; public lobbies roll them, hosts and solo players pick them
export const MODS = {
  rush: { name: 'Early rush', desc: 'Rush hour starts at 2:00 instead of 6:00.', set: c => { c.rushAt = 120; } },
  gold: { name: 'Gold rush', desc: 'Everyone starts with twice the coins, and bounties are doubled.', set: c => { c.startCoins *= 2; c.bounty *= 2; } },
  express: { name: 'Express', desc: 'Trains run 40% faster.', set: c => { c.trainSpeed *= 1.4; } },
  tight: { name: 'Tight platforms', desc: 'Every platform holds 3 fewer shapes before it overflows.', set: c => { for (const k in c.cap) c.cap[k] -= 3; } },
  tolls: { name: 'Toll roads', desc: 'Passing a rival\'s shapes through your city pays three times the toll.', set: c => { c.toll *= 3; } },
  long: { name: 'Long trains', desc: 'Trains start with 9 seats and grow to 18.', set: c => { c.trainCap = 9; c.trainCapMax = 18; } },
  busy: { name: 'Busy factories', desc: 'Every factory makes shapes 30% faster.', set: c => { c.prodInterval /= 1.3; } },
  sale: { name: 'Line sale', desc: 'Start with an extra line, and new lines cost half.', set: c => { c.startLines += 1; c.lineCost /= 2; } },
};
export const MOD_MAX = 3;
export const cleanMods = m => (Array.isArray(m) ? [...new Set(m.filter(id => typeof id === 'string' && Object.hasOwn(MODS, id)))].slice(0, MOD_MAX) : []);
// the rules in force follow whichever match is current: setState rebuilds CFG from the defaults plus its modifiers
const BASE = JSON.stringify(CFG);
let modsKey = '';
function applyMods(ids) {
  const key = (ids || []).join(',');
  if (key === modsKey) return;
  modsKey = key;
  Object.assign(CFG, JSON.parse(BASE));
  for (const id of ids || []) MODS[id].set(CFG);
}

// ---------- utilities ----------
export function mulberry32(a) { return function () { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
export const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
export function segDist(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y, l = dx * dx + dy * dy || 1;
  const t = clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / l, 0, 1);
  return Math.hypot(p.x - a.x - dx * t, p.y - a.y - dy * t);
}
export function shade(hex, k) { const n = parseInt(hex.slice(1), 16), f = c => Math.round(c * k); return `rgb(${f(n >> 16)},${f(n >> 8 & 255)},${f(n & 255)})`; }
export function rgba(hex, a) { const n = parseInt(hex.slice(1), 16); return `rgba(${n >> 16},${n >> 8 & 255},${n & 255},${a})`; }
export class Heap {
  constructor() { this.k = []; this.v = []; }
  get size() { return this.k.length; }
  push(key, val) {
    const k = this.k, v = this.v; let i = k.length; k.push(key); v.push(val);
    while (i > 0) { const p = (i - 1) >> 1; if (k[p] <= key) break; k[i] = k[p]; v[i] = v[p]; i = p; }
    k[i] = key; v[i] = val;
  }
  pop() {
    const k = this.k, v = this.v, rk = k[0], rv = v[0], lk = k.pop(), lv = v.pop(), n = k.length;
    if (n) {
      let i = 0;
      for (;;) { let c = 2 * i + 1; if (c >= n) break; if (c + 1 < n && k[c + 1] < k[c]) c++; if (k[c] >= lk) break; k[i] = k[c]; v[i] = v[c]; i = c; }
      k[i] = lk; v[i] = lv;
    }
    return [rk, rv];
  }
}
export function valueNoise(rnd) {
  const S = 64, t = Float32Array.from({ length: S * S }, () => rnd()), sm = x => x * x * (3 - 2 * x);
  const g = (i, j) => t[((j % S + S) % S) * S + ((i % S + S) % S)];
  return (x, y) => {
    const xi = Math.floor(x), yi = Math.floor(y), xf = sm(x - xi), yf = sm(y - yi);
    const a = g(xi, yi), b = g(xi + 1, yi), c = g(xi, yi + 1), d = g(xi + 1, yi + 1);
    return a + (b - a) * xf + (c - a) * yf + (a - b - c + d) * xf * yf;
  };
}
export function fbm(n, x, y, oct) { let s = 0, a = 1, f = 1, t = 0; for (let i = 0; i < oct; i++) { s += a * n(x * f + i * 17.3, y * f + i * 9.1); t += a; a *= 0.5; f *= 2; } return s / t; }

// ---------- map: a noise island split into one territory per player ----------
export const CELL = 10;
export let GW = W / CELL, GH = H / CELL;
export const DIRS = [[1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1], [1, 1, 1.414], [1, -1, 1.414], [-1, 1, 1.414], [-1, -1, 1.414]];
export const cellXY = k => ({ x: (k % GW + 0.5) * CELL, y: (Math.floor(k / GW) + 0.5) * CELL });
export function components(arr, val) {
  const seen = new Uint8Array(arr.length), out = [];
  for (let s = 0; s < arr.length; s++) {
    if (seen[s] || arr[s] !== val) continue;
    const comp = [s], q = [s]; seen[s] = 1;
    while (q.length) {
      const k = q.pop(), i = k % GW, j = (k - i) / GW;
      for (const [di, dj] of DIRS.slice(0, 4)) {
        const ni = i + di, nj = j + dj; if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) continue;
        const nk = nj * GW + ni; if (seen[nk] || arr[nk] !== val) continue;
        seen[nk] = 1; comp.push(nk); q.push(nk);
      }
    }
    out.push(comp);
  }
  return out;
}
// territories grow outward from seeds over rough ground, so borders wander like real ones
export function grow(land, seeds, rough, w) {
  const N = GW * GH, d = new Float64Array(N).fill(Infinity), own = new Int16Array(N).fill(-1), h = new Heap();
  seeds.forEach((s, r) => { d[s] = 0; own[s] = r; h.push(0, s); });
  while (h.size) {
    const [dk, k] = h.pop();
    if (dk > d[k]) continue;
    const i = k % GW, j = (k - i) / GW, r = own[k];
    for (const [di, dj, m] of DIRS) {
      const ni = i + di, nj = j + dj; if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) continue;
      const nk = nj * GW + ni; if (!land[nk]) continue;
      const nd = dk + m * rough[nk] * w[r];
      if (nd < d[nk]) { d[nk] = nd; own[nk] = r; h.push(nd, nk); }
    }
  }
  return own;
}
// marching squares over the cell grid, then Chaikin smoothing: closed outline loops for a mask
export function contourLoops(test) {
  const key = (x, y) => (x + 8) * 4096 + (y + 8);
  const dec = q => ({ x: (Math.floor(q / 4096) - 8) * CELL / 2, y: (q % 4096 - 8) * CELL / 2 });
  const adj = new Map();
  const add = (p, q) => { if (!adj.has(p)) adj.set(p, []); if (!adj.has(q)) adj.set(q, []); adj.get(p).push(q); adj.get(q).push(p); };
  const m = (i, j) => (i >= 0 && j >= 0 && i < GW && j < GH && test(j * GW + i) ? 1 : 0);
  for (let j = -1; j < GH; j++) for (let i = -1; i < GW; i++) {
    const idx = m(i, j) * 8 + m(i + 1, j) * 4 + m(i + 1, j + 1) * 2 + m(i, j + 1);
    if (idx === 0 || idx === 15) continue;
    const T = key(2 * i + 2, 2 * j + 1), R = key(2 * i + 3, 2 * j + 2), B = key(2 * i + 2, 2 * j + 3), L = key(2 * i + 1, 2 * j + 2);
    switch (idx) {
      case 1: case 14: add(L, B); break;
      case 2: case 13: add(B, R); break;
      case 3: case 12: add(L, R); break;
      case 4: case 11: add(T, R); break;
      case 6: case 9: add(T, B); break;
      case 7: case 8: add(L, T); break;
      case 5: add(L, T); add(B, R); break;
      case 10: add(T, R); add(L, B); break;
    }
  }
  const loops = [], used = new Set();
  for (const start of adj.keys()) {
    if (used.has(start)) continue;
    const loop = []; let prev = null, cur = start;
    for (;;) {
      used.add(cur); loop.push(dec(cur));
      const nbs = adj.get(cur), nx = nbs[0] !== prev ? nbs[0] : nbs[1];
      if (nx === undefined || used.has(nx)) break;
      prev = cur; cur = nx;
    }
    // thin out cell-level zigzags before rounding the outline
    if (loop.length >= 4) loops.push(chaikin(loop.length > 24 ? loop.filter((_, i) => i % 3 === 0) : loop, 3));
  }
  return loops;
}
export function chaikin(pts, it) {
  for (let n = 0; n < it; n++) {
    const out = [];
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i], q = pts[(i + 1) % pts.length];
      out.push({ x: p.x * 0.75 + q.x * 0.25, y: p.y * 0.75 + q.y * 0.25 }, { x: p.x * 0.25 + q.x * 0.75, y: p.y * 0.25 + q.y * 0.75 });
    }
    pts = out;
  }
  return pts;
}

// ---------- landforms: each map is one of these, so islands do not all look alike ----------
// shape(dx, dy) is how strongly the land rises there (dx, dy run -1..1 across the world); higher means land
const LANDFORMS = [
  { id: 'island', word: 'Island', shape: (dx, dy) => -Math.pow(Math.hypot(dx, dy * 1.05), 2.4) * 0.8 },
  { id: 'twin', word: 'Twin Isles', setup: r => ({ a: -0.42 + r() * 0.08, tilt: (r() - 0.5) * 0.5 }),
    shape: (dx, dy, o) => {
      const d1 = Math.hypot((dx - o.a) * 1.15, dy - o.tilt * 0.6), d2 = Math.hypot((dx + o.a) * 1.15, dy + o.tilt * 0.6);
      const bridge = Math.abs(dy - o.tilt * dx * -1.4) < 0.16 && Math.abs(dx) < -o.a ? 0.35 : 0;   // an isthmus keeps it one land
      return -Math.pow(Math.min(d1, d2) * 1.55, 2.2) * 0.8 + bridge;
    } },
  { id: 'bay', word: 'Bay', setup: r => { const a = r() * Math.PI * 2; return { bx: Math.cos(a) * 0.55, by: Math.sin(a) * 0.5 }; },
    shape: (dx, dy, o) => -Math.pow(Math.hypot(dx, dy * 1.05), 2.4) * 0.8 - 1.5 * Math.max(0, 1 - Math.hypot(dx - o.bx, dy - o.by) / 0.5) },
  { id: 'lagoon', word: 'Lagoon', shape: (dx, dy) => { const r = Math.hypot(dx, dy * 1.05); return -Math.pow(r, 2.4) * 0.8 - 1.6 * Math.max(0, 1 - r / 0.3); } },
  { id: 'coast', word: 'Coast', setup: r => ({ a: (r() - 0.5) * 1.2 }),
    shape: (dx, dy, o) => { const u = dx * Math.cos(o.a) + dy * Math.sin(o.a), v = -dx * Math.sin(o.a) + dy * Math.cos(o.a); return -Math.pow(Math.hypot(u * 1.05, v * 2.1), 2.3) * 0.9; } },   // long and narrow, but clear of the world's edge
  { id: 'fjords', word: 'Fjords', setup: r => ({ k: 4 + Math.floor(r() * 3), ph: r() * 6.3 }),
    shape: (dx, dy, o) => { const r = Math.hypot(dx, dy * 1.05), t = Math.atan2(dy, dx); return -Math.pow(r / (1 + 0.22 * Math.sin(o.k * t + o.ph)), 2.4) * 0.8; } },
];
const PLACE = ['Ashby', 'Kettle', 'Marrow', 'Penhallow', 'Quill', 'Rookmoor', 'Saltash', 'Thistle', 'Umber', 'Vane', 'Wexley', 'Yarrow', 'Brindle', 'Corran', 'Dunmore', 'Elsby', 'Farrow', 'Gorse', 'Hollin', 'Ivel', 'Juniper', 'Larch', 'Mossley', 'Netherby'];
// a well-mixed number from a seed, so neighbouring seeds pick unrelated landforms and names
const hash = (seed, salt) => { let h = Math.imul(seed ^ salt, 0x9e3779b1); h ^= h >>> 15; h = Math.imul(h, 0x85ebca77); h ^= h >>> 13; return (h >>> 0) / 4294967296; };
// about four maps in ten are real places, cut from real coastlines (src/sim/places.js); the rest are invented
const REAL = { id: 'real', word: '' };
const placeOf = seed => PLACES[Math.floor(hash(seed, 0x6a09e667) * PLACES.length)];
export function landformOf(seed) { return hash(seed, 0x3c6ef372) < 0.4 ? REAL : LANDFORMS[Math.floor(hash(seed, 0x2c1b3c6d) * LANDFORMS.length)]; }
export function mapName(seed) { const f = landformOf(seed); return f === REAL ? placeOf(seed).name : `${PLACE[Math.floor(hash(seed, 0x51ed270b) * PLACE.length)]} ${f.word}`; }
// a real coastline filled onto the grid, row by row: a cell is land if its centre is inside the outline
function realLand(place, np) {
  const target = 1600 * 1000 * Math.max(1, np / 6) * clamp(0.3 + 0.05 * Math.min(np, 6), 0.35, 0.6);   // land the cities need
  let area = 0; const P = place.pts;
  for (let i = 0; i < P.length; i++) { const a = P[i], b = P[(i + 1) % P.length]; area += a[0] * b[1] - b[0] * a[1]; }
  const k = Math.sqrt(target / Math.abs(area / 2)), m = 1 / 0.84;   // pixels per unit, and a margin of sea all round
  setDims(Math.max(400, Math.round(place.w * k * m / 10) * 10), Math.max(400, Math.round(place.h * k * m / 10) * 10));
  const ox = (W - place.w * k) / 2, oy = (H - place.h * k) / 2, land = new Uint8Array(GW * GH);
  for (let j = 0; j < GH; j++) {
    const y = (j + 0.5) * CELL, xs = [];
    for (let i = 0; i < P.length; i++) {
      const ay = oy + P[i][1] * k, by = oy + P[(i + 1) % P.length][1] * k;
      if ((ay > y) === (by > y)) continue;
      const ax = ox + P[i][0] * k, bx = ox + P[(i + 1) % P.length][0] * k;
      xs.push(ax + (y - ay) / (by - ay) * (bx - ax));
    }
    xs.sort((a, b) => a - b);
    for (let n = 0; n + 1 < xs.length; n += 2) for (let i = Math.max(0, Math.ceil(xs[n] / CELL - 0.5)); i < GW && (i + 0.5) * CELL < xs[n + 1]; i++) land[j * GW + i] = 1;
  }
  return land;
}

// a map whose territories came out lopsided tries fresh terrain (same landform and name) up to three times
export function genMap(seed, np) {
  let m;
  for (let k = 0; k < 4; k++) { m = genMapOnce(seed, np, k); if (m.balance < 1.55) return m; }
  // a real coastline too ragged to split fairly for this many cities: an invented one instead
  if (m.form === 'real') for (let k = 0; k < 4; k++) { m = genMapOnce(seed, np, k, true); if (m.balance < 1.55) break; }
  return m;
}
function genMapOnce(seed, np, attempt, invent = false) {
  const { w: ww, h: wh } = worldSize(np); setDims(ww, wh);
  const rnd = mulberry32(seed + attempt * 7919), nA = valueNoise(rnd), nB = valueNoise(rnd);
  const form = invent ? LANDFORMS[Math.floor(hash(seed, 0x2c1b3c6d) * LANDFORMS.length)] : landformOf(seed), fr = mulberry32(seed ^ 0x7f4a7c15), opt = form.setup ? form.setup(fr) : {};
  const real = form === REAL ? realLand(placeOf(seed), np) : null;
  // coastline character: gentle or ragged, sized to the world so a big map is not just a busier small one
  const scale = (16 + fr() * 14) * (GW / 160), oct = fr() < 0.5 ? 3 : 4, amp = 0.75 + fr() * 0.5;
  const N = GW * GH;
  let land = real;
  if (!land) {
    const v = new Float32Array(N);
    for (let j = 0; j < GH; j++) for (let i = 0; i < GW; i++) {
      const dx = (i / GW - 0.5) * 2, dy = (j / GH - 0.5) * 2;
      const edge = i < 3 || j < 3 || i >= GW - 3 || j >= GH - 3;
      // every landform sinks to sea near the world's edge, so no coastline is ever cut off straight
      const rim = Math.min(1 - Math.abs(dx), 1 - Math.abs(dy)), sink = 3 * Math.max(0, 1 - rim / 0.16) ** 2;
      v[j * GW + i] = edge ? -9 : amp * fbm(nA, i / scale, j / scale, oct) + form.shape(dx, dy, opt) - sink;
    }
    // land share grows with player count up to six, past that the world itself grows
    const frac = clamp(0.3 + 0.05 * Math.min(np, 6), 0.35, 0.6) / SEA_ROOM;   // same land per city, more sea
    const th = Float32Array.from(v).sort()[Math.floor(N * (1 - frac))];
    land = new Uint8Array(N);
    for (let k = 0; k < N; k++) land[k] = v[k] >= th ? 1 : 0;
  }
  const isles = components(land, 1).sort((a, b) => b.length - a.length);
  land.fill(0); for (const k of isles[0]) land[k] = 1;
  for (const lake of components(land, 0)) {
    const touchesEdge = lake.some(k => { const i = k % GW, j = (k - i) / GW; return i === 0 || j === 0 || i === GW - 1 || j === GH - 1; });
    if (!touchesEdge && lake.length < 60 * (GW * GH) / 16000) for (const k of lake) land[k] = 1;
  }
  const cells = []; for (let k = 0; k < N; k++) if (land[k]) cells.push(k);
  let seeds = [cells[Math.floor(rnd() * cells.length)]];
  while (seeds.length < np) {
    let best = -1, bd = -1;
    for (let t = 0; t < 500; t++) {
      const k = cells[Math.floor(rnd() * cells.length)], d = Math.min(...seeds.map(s => dist(cellXY(s), cellXY(k))));
      if (d > bd) { bd = d; best = k; }
    }
    seeds.push(best);
  }
  const rough = new Float32Array(N);
  for (let k = 0; k < N; k++) rough[k] = 1 + 2.2 * fbm(nB, (k % GW) / 11, Math.floor(k / GW) / 11, 2);
  // relax seeds toward their centres and re-weight growth until territories are close in size
  const w = new Array(np).fill(1);
  let own, balance = Infinity;
  for (let it = 0; it < 10; it++) {
    own = grow(land, seeds, rough, w);
    const area = new Array(np).fill(0), sx = new Array(np).fill(0), sy = new Array(np).fill(0);
    for (const k of cells) { const r = own[k]; if (r < 0) continue; area[r]++; sx[r] += k % GW; sy[r] += Math.floor(k / GW); }
    if (it === 9) { balance = Math.max(...area) / Math.max(1, Math.min(...area)); break; }
    const mean = cells.length / np;
    for (let r = 0; r < np; r++) w[r] *= Math.pow(Math.max(1, area[r]) / mean, 0.6);
    if (it < 4) seeds = seeds.map((s, r) => {
      const ti = sx[r] / area[r], tj = sy[r] / area[r];
      let best = s, bd = Infinity;
      for (const k of cells) if (own[k] === r) { const d = (k % GW - ti) ** 2 + (Math.floor(k / GW) - tj) ** 2; if (d < bd) { bd = d; best = k; } }
      return best;
    });
  }
  // distance (in cells) from each cell to its territory's edge
  const df = new Int16Array(N).fill(-1), q = [];
  for (const k of cells) {
    const i = k % GW, j = (k - i) / GW;
    const edge = DIRS.slice(0, 4).some(([di, dj]) => { const ni = i + di, nj = j + dj; return ni < 0 || nj < 0 || ni >= GW || nj >= GH || own[nj * GW + ni] !== own[k]; });
    if (edge) { df[k] = 0; q.push(k); }
  }
  for (let h = 0; h < q.length; h++) {
    const k = q[h], i = k % GW, j = (k - i) / GW;
    for (const [di, dj] of DIRS) {
      const ni = i + di, nj = j + dj; if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) continue;
      const nk = nj * GW + ni; if (df[nk] !== -1 || own[nk] !== own[k]) continue;
      df[nk] = df[k] + 1; q.push(nk);
    }
  }
  const regions = [];
  for (let r = 0; r < np; r++) {
    let best = -1, bd = -1;
    for (const k of cells) if (own[k] === r && df[k] > bd) { bd = df[k]; best = k; }
    const loops = contourLoops(k => own[k] === r);
    regions.push({ id: r, c: cellXY(best), hearts: [cellXY(best)], loops, owner: null, dead: false, nb: [] });
  }
  // shared borders become gate pairs
  const pairs = new Map();
  for (const k of cells) {
    const i = k % GW, j = (k - i) / GW;
    for (const nk of [i + 1 < GW ? k + 1 : -1, j + 1 < GH ? k + GW : -1]) {
      if (nk < 0 || own[nk] < 0 || own[nk] === own[k]) continue;
      const a = Math.min(own[k], own[nk]), b = Math.max(own[k], own[nk]), key = a * 64 + b;
      if (!pairs.has(key)) pairs.set(key, []);
      const p1 = cellXY(k), p2 = cellXY(nk);
      pairs.get(key).push({ x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 });
    }
  }
  const gatesOf = regions.map(() => []);
  const pickGate = (r, mid) => {
    let best = null, bd = Infinity;
    for (const k of cells) {
      if (own[k] !== r || df[k] < 3) continue;
      const p = cellXY(k), d = dist(p, mid);
      if (d > 110 || d >= bd || gatesOf[r].some(g => dist(g, p) < 46)) continue;
      bd = d; best = p;
    }
    return best;
  };
  let pidN = 0;
  for (const [key, pts] of pairs) {
    if (pts.length < 6) continue;
    const a = Math.floor(key / 64), b = key % 64;
    const mx = pts.reduce((s, p) => s + p.x, 0) / pts.length, my = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    let mid = pts[0]; for (const p of pts) if (dist(p, { x: mx, y: my }) < dist(mid, { x: mx, y: my })) mid = p;
    const ga = pickGate(a, mid), gb = pickGate(b, mid);
    if (!ga || !gb) continue;
    gatesOf[a].push(ga); gatesOf[b].push(gb);
    regions[a].nb.push({ to: b, gate: ga, pid: pidN });
    regions[b].nb.push({ to: a, gate: gb, pid: pidN++ });
  }
  let x0 = W, y0 = H, x1 = 0, y1 = 0;
  for (const k of cells) { const p = cellXY(k); x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
  const landLoops = contourLoops(k => land[k] === 1);
  // a city with no neighbour cannot trade at all: that map counts as unusable
  if (regions.some(r => !r.nb.length)) balance = Infinity;
  return { regions, own, df, landLoops, balance, w: W, h: H, name: invent ? `${PLACE[Math.floor(hash(seed, 0x51ed270b) * PLACE.length)]} ${form.word}` : mapName(seed), form: form.id, bounds: { x: x0 - 40, y: y0 - 40, w: x1 - x0 + 80, h: y1 - y0 + 80 } };
}
export function regionAt(pt) {
  const i = Math.floor(pt.x / CELL), j = Math.floor(pt.y / CELL);
  if (i < 0 || j < 0 || i >= GW || j >= GH) return null;
  const r = G.map.own[j * GW + i];
  return r < 0 ? null : G.regions[r];
}

// ---------- game state ----------
// G is the match being simulated or viewed right now. The server swaps it per room before ticking;
// the client swaps it to the latest snapshot before drawing. Everything below reads it.
export let G = null;
export function setState(s) { G = s; if (s) { applyMods(s.mods); if (s.map && s.map.w) setDims(s.map.w, s.map.h); } }

// seats: [{ name, human }] in seat order; a seat's index is its player id and decides its shape and colour
// bot difficulty. think: seconds between decisions; hunt: chance a bot goes after a struggling city, and how far it looks;
// lean: most a rival's wealth can pull a bot's aim; slack: spare coins a bot keeps before buying; rateCap: most a bot factory makes
export const LEVELS = {
  easy: { name: 'Easy', desc: 'Bots think slowly, never gang up and keep their factories small.', think: 2, hunt: 0, huntHops: 0, huntCrowd: 1, lean: 0, slack: 40, rateCap: 4, surges: false },
  normal: { name: 'Normal', desc: 'Bots defend well and sometimes go after a nearby city that is struggling.', think: 1, hunt: 0.3, huntHops: 3, huntCrowd: 0.3, lean: 1.2, slack: 20, rateCap: 10, surges: true },
  hard: { name: 'Hard', desc: 'Bots decide fast, hunt struggling cities from further away and gang up on the leader.', think: 0.7, hunt: 0.5, huntHops: 5, huntCrowd: 0.2, lean: 2.5, slack: 5, rateCap: 10, surges: true },
};
export const cleanLevel = v => (Object.prototype.hasOwnProperty.call(LEVELS, v) ? v : 'normal');
const LV = () => LEVELS[G.level] || LEVELS.normal;
export function createGame(seed, seats, opts = {}) {
  const np = seats.length, map = genMap(seed, np);
  return {
    phase: 'draft', seed, np, map, regions: map.regions, players: [], stations: new Map(),
    seats: seats.map((s, i) => ({ name: s.name || defaultName(i), human: !!s.human, claim: null })),
    flights: [], events: [], rd: {}, xq: {}, netV: 1, geoV: 0, mapV: 1, time: 0, sid: 1, uid: 1, lid: 1,
    rngState: (seed ^ 0x5bd1e995) >>> 0, rng: null, cheats: !!opts.cheats, mods: cleanMods(opts.mods), level: cleanLevel(opts.level), winner: null,
  };
}
// a seeded random stream that lives in the state, so a match can be replayed and the server stays authoritative
export function rand() {
  let a = (G.rngState = (G.rngState + 0x6D2B79F5) | 0);
  let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
  return ((t ^ t >>> 14) >>> 0) / 4294967296;
}
export function emit(ev) { G.events.push({ ...ev, t: G.time }); }
// the headless audit (scripts/audit.mjs) counts every way a shape leaves play; off unless G.audit is set
export function sink(why, n = 1) { if (G.audit) G.audit[why] = (G.audit[why] || 0) + n; }

export function claimRegion(seat, rid) {
  if (G.phase !== 'draft' || !G.seats[seat] || !G.regions[rid]) return false;
  if (G.seats.some((s, i) => i !== seat && s.claim === rid)) return false;
  G.seats[seat].claim = rid;
  return true;
}
export function startMatch() {
  if (G.phase !== 'draft') return;
  const regions = G.regions, rnd = mulberry32(G.seed ^ 0x9e3779b9);
  const claimed = G.seats.map(s => s.claim);
  // unclaimed seats spread out: each takes the free region farthest from everything already claimed
  const order = G.seats.map((s, i) => i).filter(i => claimed[i] == null);
  for (let k = order.length - 1; k > 0; k--) { const j = Math.floor(rnd() * (k + 1)); [order[k], order[j]] = [order[j], order[k]]; }
  for (const i of order) {
    let best = null, bd = -1;
    const taken = claimed.filter(c => c != null);
    for (const r of regions) {
      if (taken.includes(r.id)) continue;
      const d = (taken.length ? Math.min(...taken.map(c => dist(regions[c].c, r.c))) : 0) + rnd() * 120;
      if (d > bd) { bd = d; best = r.id; }
    }
    claimed[i] = best;
  }
  claimed.forEach((rid, i) => makePlayer(i, rid, G.seats[i].human, G.seats[i].name));
  G.phase = 'play';
  emit({ type: 'start' });
}

export function makePlayer(id, rid, isHuman, name) {
  const r = G.regions[rid]; r.owner = id;
  const p = {
    id, shape: shapeOf(id), color: PCOL[id], name: name || defaultName(id), isHuman, connected: isHuman, region: rid, alive: true,
    coins: CFG.startCoins, lineLimit: CFG.startLines, lines: [], spare: [], st: [], rate: 1, rateMax: 2, target: 'auto', lastTarget: null,
    prod: 0.999, depots: 0, adj: new Map(), dist: new Map(), botT: 2 + id * 0.3, botTarget: 0, rebuild: true,
    stats: { processed: 0, delivered: 0, passed: 0, recycled: 0, surges: 0, annexed: 0 }, bank: 0, banking: false,
  };
  G.players.push(p);
  addStation(p, 'factory', r.c.x, r.c.y, { shape: p.shape });
  for (const nb of r.nb) addGate(p, nb);
  netChanged(p);
  return p;
}

export function addGate(p, nb) {
  return addStation(p, 'gate', nb.gate.x, nb.gate.y, { to: nb.to, pid: nb.pid, ang: Math.atan2(G.regions[nb.to].c.y - nb.gate.y, G.regions[nb.to].c.x - nb.gate.x) });
}
export function addStation(p, kind, x, y, extra) {
  const s = Object.assign({ id: G.sid++, owner: p.id, kind, x, y, units: [], crowd: 0, born: G.time }, extra);
  G.stations.set(s.id, s); p.st.push(s.id); G.netV = (G.netV || 0) + 1;
  return s;
}
export const S = id => G.stations.get(id);
export const stationsOf = p => p.st.map(S);
export const capOf = s => CFG.cap[s.kind] + (s.warehouse ? 10 : 0);

// ---------- region routing ----------
// region distances to `to`; ruins are avoided, but crossed on foot when they are the only way
export function rdistBase(to, viaRuins) {
  const key = to + (viaRuins ? 'r' : '');
  if (G.rd[key]) return G.rd[key];
  const d = new Array(G.regions.length).fill(Infinity);
  if (!G.regions[to].dead) {
    d[to] = 0; const q = [to];
    while (q.length) {
      const r = q.shift();
      for (const nb of G.regions[r].nb) if ((viaRuins || !G.regions[nb.to].dead) && d[nb.to] > d[r] + 1) { d[nb.to] = d[r] + 1; q.push(nb.to); }
    }
  }
  return (G.rd[key] = d);
}
// the map's region routes changed (a city fell or land was annexed): every city's station routes depend on
// them, since which gates count as exits comes from region distances, so all of them are recomputed
export function routesChanged() {
  G.rd = {};
  for (const q of G.players) if (q.dist) q.dist.clear();
}
export function rdist(to, from) { const d = rdistBase(to, false); return from == null || d[from] !== Infinity ? d : rdistBase(to, true); }
export function hops(from, to) { const d = rdist(to, from)[from]; return d === Infinity ? null : d; }
export function nextRegion(from, to) {
  if (from === to) return null;
  const d = rdist(to, from);
  if (d[from] === Infinity) return null;
  for (const nb of G.regions[from].nb) if (d[nb.to] === d[from] - 1) return nb.to;
  return null;
}
export function retarget(u, rid) {
  const r = G.regions[rid];
  if (r.owner != null && G.players[r.owner].alive && r.owner !== u.sender) { u.target = r.owner; return true; }
  let best = null, bd = Infinity;
  for (const q of G.players) {
    if (!q.alive || q.id === u.sender) continue;
    const h = hops(rid, q.region);
    if (h != null && h < bd) { bd = h; best = q.id; }
  }
  if (best == null) return false;
  u.target = best; return true;
}
// what a unit currently inside player p's city wants: 'd:<shape>' (a depot) or 'g:<region>' (a gate), or null (gone)
export function goalOf(u, p) {
  for (let k = 0; k < 3; k++) {
    const tp = G.players[u.target];
    if (!tp || !tp.alive) { if (!retarget(u, p.region)) return null; continue; }
    if (u.target === p.id) return 'd:' + u.shape;
    if (nextRegion(p.region, tp.region) == null) { if (!retarget(u, p.region)) return null; continue; }
    // only exits that get the shape closer to its target count, never straight back: a sideways exit
    // would hand it to whichever neighbour is nearest, which looks like the Send-to choice being ignored
    return `x:${tp.region}:${u.from ?? -1}`;
  }
  return null;
}
export function exitOK(fromRid, key, toRid) {
  const [, T, back] = key.split(':').map(Number);
  if (toRid === T) return true;
  const d = rdist(T, fromRid);
  if (!(d[toRid] < d[fromRid])) return false;
  // never straight back where it came from, unless that is now the only way forward (its target moved behind it)
  return toRid !== back || !G.regions[fromRid].nb.some(nb => nb.to !== back && d[nb.to] < d[fromRid]);
}
export const isGoalStation = (s, key) => ((s.kind === 'depot' || s.kind === 'recycler') && key === 'd:' + s.shape) ||
  (s.kind === 'gate' && key[0] === 'x' && exitOK(G.players[s.owner].region, key, s.to));

// ---------- station network routing ----------
export function netChanged(p) {
  G.netV = (G.netV || 0) + 1;
  rebuildAdj(p);
}
export function rebuildAdj(p) {
  p.adj = new Map();
  const link = (a, b) => { if (!p.adj.has(a)) p.adj.set(a, new Set()); if (!p.adj.has(b)) p.adj.set(b, new Set()); p.adj.get(a).add(b); p.adj.get(b).add(a); };
  for (const L of p.lines) {
    const n = L.st.length;
    for (let i = 0; i + 1 < n; i++) link(L.st[i], L.st[i + 1]);
    if (L.loop && n > 2) link(L.st[n - 1], L.st[0]);
  }
  p.dist.clear();
}
export function distMap(p, key) {
  let m = p.dist.get(key);
  if (m) return m;
  m = new Map(); const q = [];
  for (const id of p.st) if (isGoalStation(S(id), key)) { m.set(id, 0); q.push(id); }
  while (q.length) {
    const id = q.shift(), d = m.get(id);
    for (const nb of (p.adj.get(id) || [])) if (!m.has(nb)) { m.set(nb, d + 1); q.push(nb); }
  }
  p.dist.set(key, m); return m;
}
export const dOf = (m, id) => (m.has(id) ? m.get(id) : Infinity);

// ---------- lines & trains ----------
export function newTrain(idx = 0) { return { idx, to: null, t: 0, dir: 1, wait: 0.3, units: [], cap: CFG.trainCap, fresh: true }; }
// an extra carriage: +3 seats, pricier each time the same train grows
export const carCost = T => CFG.carCost * (1 + (T.cap - CFG.trainCap) / CFG.carSeats);
export const upgradeable = L => L.trains.filter(T => T.cap < CFG.trainCapMax);
export function addCarriage(p, L) {
  const pool = upgradeable(L);
  if (!pool.length) return null;
  const T = pool[Math.floor(rand() * pool.length)];
  if (p.coins < carCost(T)) return null;
  p.coins -= carCost(T); T.cap += CFG.carSeats;
  return T;
}
export const seats = L => L.trains.reduce((n, T) => n + T.cap, 0);
export function createLine(p, st) {
  const used = new Set(p.lines.map(l => l.color));
  const order = LINE_COLORS.map((_, i) => LINE_COLORS[(i + p.id * 2) % LINE_COLORS.length]);
  const color = order.find(c => !used.has(c)) || order[p.lines.length % order.length];
  const L = { id: G.lid++, owner: p.id, color, st: st.slice(), loop: false, trains: [newTrain()] };
  p.lines.push(L); netChanged(p);
  return L;
}
export function removeLine(p, L) {
  for (const T of L.trains) { const s = S(L.st[T.idx]); if (s) s.units.push(...T.units); }
  // the line's own train goes with it; every train bought for it waits in the sidings, carriages and all
  const caps = L.trains.map(T => T.cap).sort((a, b) => b - a).slice(0, -1);
  p.spare = [...(p.spare || []), ...caps].sort((a, b) => b - a);
  p.lines = p.lines.filter(l => l !== L);
  netChanged(p);
  return caps.length;
}
// a train for line L: a spare from the sidings if there is one (biggest first), else bought; false if neither
export function takeTrain(p, L) {
  if (p.spare && p.spare.length) { addTrain(L, Math.floor(L.st.length / 2)).cap = p.spare.shift(); return 'spare'; }
  if (p.coins < CFG.trainCost) return false;
  p.coins -= CFG.trainCost; addTrain(L, Math.floor(L.st.length / 2));
  return 'bought';
}
export function setLineStations(p, L, st) {
  for (const T of L.trains) {
    const cur = L.st[T.idx];
    let ni = st.indexOf(cur);
    if (ni < 0) { const s = S(L.st[T.idx]); if (s) s.units.push(...T.units); T.units = []; ni = 0; }
    T.idx = ni; T.to = null; T.t = 0; T.wait = 0.2;
  }
  L.st = st.slice(); L.loop = false;
  netChanged(p);
}
export function lineNext(L, idx, dir) {
  const n = L.st.length;
  if (L.loop && n > 2) return (idx + dir + n) % n;
  const j = idx + dir; return j < 0 || j >= n ? null : j;
}
// lines sharing a track between the same two stations get side-by-side lanes, centred on the track
export const LANE = 7.5;
export function buildLanes() {
  const m = new Map();
  for (const p of G.players) {
    if (!p.alive) continue;
    for (const L of p.lines) {
      const n = L.st.length;
      for (let i = 0; i < n - 1 + (L.loop && n > 2 ? 1 : 0); i++) {
        const a = L.st[i], b = L.st[(i + 1) % n], key = Math.min(a, b) + '-' + Math.max(a, b);
        if (!m.has(key)) m.set(key, []);
        if (!m.get(key).includes(L.id)) m.get(key).push(L.id);
      }
    }
  }
  G.lanes = m;
}
export function laneOff(L, a, b) {
  const lanes = G.lanes && G.lanes.get(Math.min(a, b) + '-' + Math.max(a, b));
  if (!lanes) return 0;
  const off = (lanes.indexOf(L.id) - (lanes.length - 1) / 2) * LANE;
  return a < b ? off : -off;
}

export function updateTrain(p, L, T, dt) {
  if (L.st.length < 2) return;
  if (T.idx >= L.st.length) T.idx = L.st.length - 1;
  if (T.to == null) {
    T.wait -= dt;
    if (T.wait > 0) return;
    // a brand-new train loads at the station it starts on before it leaves
    if (T.fresh) { T.fresh = false; trainArrive(p, L, T); return; }
    let nx = lineNext(L, T.idx, T.dir);
    if (nx == null) { T.dir *= -1; nx = lineNext(L, T.idx, T.dir); }
    T.to = nx; T.t = 0;
    return;
  }
  T.t += CFG.trainSpeed * dt / Math.max(1, trainSeg(L, T.idx, T.to).geo.len);
  if (T.t >= 1) { T.idx = T.to; T.to = null; T.t = 0; trainArrive(p, L, T); }
}
// best distance-to-goal reachable by staying on this train: around the whole loop, or out to the terminus
export function aheadBest(L, idx, dir, m) {
  let best = Infinity, i = idx;
  for (let k = 0; k < L.st.length - 1; k++) {
    const j = lineNext(L, i, dir);
    if (j == null) break;
    best = Math.min(best, dOf(m, L.st[j])); i = j;
  }
  return best;
}
// stops until this train reaches somewhere closer to the goal than `ds`
export function aheadStops(L, idx, dir, m, ds) {
  let i = idx;
  for (let k = 1; k < L.st.length; k++) {
    const j = lineNext(L, i, dir);
    if (j == null) return Infinity;
    if (dOf(m, L.st[j]) < ds) return k;
    i = j;
  }
  return Infinity;
}
// on a loop with trains running both ways, nobody needs to ride the long way round
export const counterTrain = (L, T) => L.loop && L.trains.some(o => o !== T && o.dir !== T.dir);
export function addTrain(L, idx) {
  const T = newTrain(idx);
  if (L.loop) { const fwd = L.trains.filter(t => t.dir === 1).length; T.dir = fwd > L.trains.length - fwd ? -1 : 1; }
  L.trains.push(T);
  return T;
}
export function trainArrive(p, L, T) {
  const st = S(L.st[T.idx]);
  let nx = lineNext(L, T.idx, T.dir);
  if (nx == null) { T.dir *= -1; nx = lineNext(L, T.idx, T.dir); }
  const nextId = nx == null ? null : L.st[nx];
  let moved = 0;
  for (let k = T.units.length - 1; k >= 0; k--) {
    const u = T.units[k], g = goalOf(u, p);
    if (g == null) { T.units.splice(k, 1); sink('noTarget'); continue; }
    const m = distMap(p, g), ds = dOf(m, st.id);
    if (ds === 0 || !(aheadBest(L, T.idx, T.dir, m) < ds)) { T.units.splice(k, 1); st.units.push(u); moved++; }
  }
  if (nextId != null && T.units.length < T.cap) {
    // leaving a shape for the train running the short way is only a courtesy: once the platform fills past
    // half, any train that gets it closer takes it (shortest rides first), or a full counter-train livelocks it
    const busy = st.units.length >= capOf(st) / 2;
    const takers = [];
    for (const u of st.units) {
      const g = goalOf(u, p); if (g == null) continue;
      const m = distMap(p, g), ds = dOf(m, st.id);
      if (!(ds > 0)) continue;
      const stops = aheadStops(L, T.idx, T.dir, m, ds);
      if (stops === Infinity || (stops > ds + 1 && counterTrain(L, T) && !busy)) continue;
      takers.push([stops, u]);
    }
    takers.sort((a, b) => a[0] - b[0]);
    for (const [, u] of takers) {
      if (T.units.length >= T.cap) break;
      st.units.splice(st.units.indexOf(u), 1); T.units.push(u); moved++;
    }
  }
  T.wait = 0.3 + moved * 0.06;
}

// ---------- flights (units crossing borders and open country) ----------
// annexed ruins live on inside whoever took them
export function liveRegion(rid) { while (G.regions[rid].mergedInto != null) rid = G.regions[rid].mergedInto; return rid; }
export function crossBorder(u, fromRid, toRid, x, y, pid) {
  fromRid = liveRegion(fromRid); toRid = liveRegion(toRid);
  const nbs = G.regions[toRid].nb, nb = (pid != null && nbs.find(n => n.pid === pid)) || nbs.find(n => n.to === fromRid);
  if (!nb) {
    // the crossing was swallowed by an annexation: set the shape down at the owner's nearest station
    const R = G.regions[toRid], q = R.owner != null && !R.dead ? G.players[R.owner] : null;
    const near = q && stationsOf(q).sort((a, b) => Math.hypot(a.x - x, a.y - y) - Math.hypot(b.x - x, b.y - y))[0];
    if (near) near.units.push(u); else sink('lostAtCrossing');
    return;
  }
  const g = nb.gate;
  u.from = fromRid; u.hops = (u.hops || 0) + 1;
  // shapes leaving through the same crossing go single file, so a batch reads as a stream, not one dot
  const key = 'p' + nb.pid + '>' + toRid, slot = Math.max(G.time, G.xq[key] || 0);
  G.xq[key] = slot + 0.12;
  // a customs post on the receiving gate makes rival shapes cross at half speed
  const R = G.regions[toRid], owner = R.owner != null && !R.dead ? G.players[R.owner] : null;
  const booth = owner && owner.id !== u.sender && stationsOf(owner).find(s => s.kind === 'gate' && s.pid === nb.pid && s.customs);
  const dur = Math.max(0.5, Math.hypot(g.x - x, g.y - y) / CFG.flightSpeed) * (booth ? 2 : 1);
  G.flights.push({ u, x0: x, y0: y, x1: g.x, y1: g.y, t: -(slot - G.time) / dur, dur, rid: toRid, from: fromRid, pid: nb.pid, phase: 'cross' });
}
export function landFlight(f) {
  f.rid = liveRegion(f.rid);
  const R = G.regions[f.rid];
  if (f.phase === 'cross' && R.owner != null && !R.dead) {
    const p = G.players[R.owner];
    const gates = stationsOf(p).filter(s => s.kind === 'gate');
    const gs = gates.find(s => s.pid === f.pid) || gates.find(s => s.to === f.from);
    if (gs) { f.u.inGate = gs.id; gs.units.push(f.u); } else sink('lostNoGate');
    return;
  }
  if (f.phase === 'cross') { // open country: walk across to the right exit
    let nr = null;
    for (let k = 0; k < 3 && nr == null; k++) {
      const tp = G.players[f.u.target];
      if (!tp || !tp.alive) { if (!retarget(f.u, R.id)) return sink('noTarget'); continue; }
      nr = nextRegion(R.id, tp.region);
      if (nr == null && !retarget(f.u, R.id)) return sink('noTarget');
    }
    if (nr == null) return sink('noRoute');
    const out = R.nb.find(n => n.to === nr), g = out.gate;
    G.flights.push({ u: f.u, x0: f.x1, y0: f.y1, x1: g.x, y1: g.y, t: 0, dur: Math.max(0.5, Math.hypot(g.x - f.x1, g.y - f.y1) / (CFG.flightSpeed * 0.55)), rid: R.id, next: nr, pid: out.pid, phase: 'wild' });
    return;
  }
  if (f.phase === 'wild') {
    // the ruins it was walking across were annexed on the way: it is inside a city now, so it joins its network
    if (R.owner != null && !R.dead) {
      const near = stationsOf(G.players[R.owner]).sort((a, b) => Math.hypot(a.x - f.x1, a.y - f.y1) - Math.hypot(b.x - f.x1, b.y - f.y1))[0];
      if (near) near.units.push(f.u); else sink('lostAtCrossing');
      return;
    }
    crossBorder(f.u, R.id, f.next, f.x1, f.y1, f.pid);
  }
}

// ---------- per-player simulation ----------
export function processStations(p, dt, dying) {
  for (const s of stationsOf(p)) {
    for (let k = s.units.length - 1; k >= 0; k--) {
      const u = s.units[k], g = goalOf(u, p);
      if (g == null) { s.units.splice(k, 1); sink('noTarget'); continue; }
      if (!isGoalStation(s, g)) continue;
      s.units.splice(k, 1);
      if (s.kind === 'depot') {
        sink('delivered');
        p.coins += 1; p.stats.processed++;
        const snd = G.players[u.sender]; if (snd.alive) { snd.coins += 1; snd.stats.delivered++; }
        emit({ type: 'pop', to: p.id, x: s.x, y: s.y - 18, text: '+1' });
      } else if (s.kind === 'recycler') {
        // no coins for anyone: the shape is remade as yours and heads back out
        sink('recycled');
        const t = chooseTarget(p);
        if (t != null) { s.units.push({ id: G.uid++, shape: p.shape, sender: p.id, target: t }); p.stats.recycled++; }
        emit({ type: 'pop', to: p.id, x: s.x, y: s.y - 18, text: '↻' });
      } else {
        if (p.id !== u.sender && p.id !== u.target) {
          // the customs bonus is for this city's own post, at the gate the shape leaves by or the one it came in by
          const inG = u.inGate != null && G.stations.get(u.inGate), ownIn = inG && inG.owner === p.id && inG.customs;
          p.coins += CFG.toll * (s.customs || ownIn ? 3 : 1); p.stats.passed++;
        }
        crossBorder(u, p.region, s.to, s.x, s.y, s.pid);
      }
    }
    if (s.units.length > capOf(s) && !(s.graceUntil > G.time)) s.crowd += dt / (CFG.overTime * (s.warehouse ? 1.8 : 1));
    else s.crowd = Math.max(0, s.crowd - dt / (CFG.overTime * 1.5));
    if (s.crowd >= 1) dying.add(p);
  }
}
export function chooseTarget(p) {
  const cands = G.players.filter(q => q.alive && q.id !== p.id && hops(p.region, q.region) != null);
  if (!cands.length) return null;
  if (p.target !== 'auto') { const q = cands.find(c => c.id === p.target); if (q) return q.id; }
  // bots avoid piling onto whoever everyone else is already hitting
  const pile = q => p.isHuman ? 0 : G.players.filter(o => o.alive && o !== p && o.lastTarget === q.id).length * 0.75;
  // and they lean toward whoever is getting rich, so a quiet corner city does not coast to the win
  // for a human, Nearest means the nearest city their lines can actually send to; an unlinked border only counts if none is linked
  const score = q => p.isHuman ? hops(p.region, q.region) + (linkedTo(p, q) ? 0 : 50) : 0.6 * hops(p.region, q.region) + pile(q) - Math.min(LV().lean, 0.02 * q.coins + 0.01 * q.stats.processed);
  cands.sort((a, b) => score(a) - score(b) || maxCrowd(b) - maxCrowd(a));
  return cands[0].id;
}
// can shapes leaving p's factory reach a gate that heads toward q, on p's own lines?
export const linkedTo = (p, q) => stationsOf(p).some(f => f.kind === 'factory' && dOf(distMap(p, `x:${q.region}:-1`), f.id) < Infinity);
// a human on Nearest: shapes still waiting at the factory for a city no line leads to switch to one that does
function readdress(p) {
  if (!p.isHuman || p.target !== 'auto') return;
  const t = chooseTarget(p); if (t == null || !linkedTo(p, G.players[t])) return;
  for (const f of stationsOf(p)) if (f.kind === 'factory') for (const u of f.units) {
    if (u.sender === p.id && u.target !== t && G.players[u.target] && !linkedTo(p, G.players[u.target])) u.target = t;
  }
}
export function produce(p, dt) {
  if (G.time < CFG.grace || p.rate <= 0) return;
  p.prod += dt * p.rate * rushHour() / CFG.prodInterval;
  while (p.prod >= 1) {
    p.prod -= 1;
    const t = chooseTarget(p); if (t == null) return;
    p.lastTarget = t; readdress(p);
    for (const f of stationsOf(p).filter(s => s.kind === 'factory')) {
      // banking stores output for a surge; once the bank is full it spills onto the platform as usual
      if (p.banking && p.bank < bankCap(p)) p.bank++;
      else f.units.push({ id: G.uid++, shape: p.shape, sender: p.id, target: t });
    }
  }
}
export const bankCap = p => 20 + 5 * p.rateMax;
// drop the whole bank (plus a bonus) onto the factory platforms for the trains to haul out;
// the platforms get a few seconds of grace so a surge doesn't overflow its own city
// a surge needs a real reserve and a breather between them, or back-to-back grace would make a factory unkillable
export const surgeReady = p => p.bank >= CFG.surgeMin && G.time >= (p.surgeAt || -Infinity) + CFG.surgeCooldown;
export function releaseSurge(p) {
  const t = chooseTarget(p);
  if (t == null || !surgeReady(p)) return 0;
  const facs = stationsOf(p).filter(s => s.kind === 'factory');
  const n = Math.floor(p.bank * CFG.surgeBonus);
  for (let i = 0; i < n; i++) facs[i % facs.length].units.push({ id: G.uid++, shape: p.shape, sender: p.id, target: t });
  for (const f of facs) f.graceUntil = G.time + CFG.surgeGrace;
  p.bank = 0; p.banking = false; p.surgeAt = G.time; p.stats.surges++; p.lastTarget = t;
  const victim = G.players[t];
  emit({ type: 'surge', from: p.id, to: victim.id, n });
  return n;
}
// late game every factory speeds up so a stalemate always breaks
export function rushHour() { return G.time < CFG.rushAt ? 1 : 1 + (G.time - CFG.rushAt) / 120; }
export function maxCrowd(p) { let m = 0; for (const s of stationsOf(p)) m = Math.max(m, s.crowd); return m; }
export function unitsIn(p) {
  const all = [];
  for (const s of stationsOf(p)) all.push(...s.units);
  for (const L of p.lines) for (const T of L.trains) all.push(...T.units);
  return all;
}

export function eliminate(p) {
  if (!p.alive) return;
  const counts = {};
  for (const u of unitsIn(p)) if (u.sender !== p.id) counts[u.sender] = (counts[u.sender] || 0) + 1;
  let killer = null, kc = 0;
  for (const k in counts) if (counts[k] > kc && G.players[k].alive) { kc = counts[k]; killer = G.players[k]; }
  sink('fellWithCity', unitsIn(p).length);
  p.alive = false;
  G.regions[p.region].dead = true;
  routesChanged();
  for (const id of p.st) G.stations.delete(id);
  p.st = []; p.lines = []; G.netV = (G.netV || 0) + 1;
  const bounty = CFG.bounty + Math.floor(p.coins / 2);
  p.coins = 0;
  if (killer) { killer.coins += bounty; }
  emit({ type: 'overrun', victim: p.id, killer: killer ? killer.id : null, bounty, x: G.regions[p.region].c.x, y: G.regions[p.region].c.y });
  const alive = G.players.filter(q => q.alive);
  if (alive.length <= 1) return endGame(alive[0]);
}
// ruins that border p and are free to annex
export function ruinsNextTo(p) { return G.regions.filter(r => r.dead && r.mergedInto == null && r.nb.some(n => n.to === p.region)); }
export function removeStationFromLines(p, id) {
  for (const L of p.lines.slice()) {
    const i = L.st.indexOf(id);
    if (i < 0) continue;
    L.st.splice(i, 1);
    for (const T of L.trains) {
      if (T.idx > i) T.idx--; else if (T.idx === i) { T.idx = Math.min(i, L.st.length - 1); T.to = null; T.wait = 0.2; }
      if (T.to != null) { if (T.to === i) { T.to = null; T.wait = 0.2; } else if (T.to > i) T.to--; }
      T.idx = Math.max(0, T.idx);
    }
    if (L.st.length < 3) L.loop = false;
    if (L.st.length < 2) removeLine(p, L);
  }
}
export function computeDF(map) {
  const N = GW * GH, own = map.own, df = new Int16Array(N).fill(-1), q = [];
  for (let k = 0; k < N; k++) {
    if (own[k] < 0) continue;
    const i = k % GW, j = (k - i) / GW;
    if (DIRS.slice(0, 4).some(([di, dj]) => { const ni = i + di, nj = j + dj; return ni < 0 || nj < 0 || ni >= GW || nj >= GH || own[nj * GW + ni] !== own[k]; })) { df[k] = 0; q.push(k); }
  }
  for (let h = 0; h < q.length; h++) {
    const k = q[h], i = k % GW, j = (k - i) / GW;
    for (const [di, dj] of DIRS) {
      const ni = i + di, nj = j + dj; if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) continue;
      const nk = nj * GW + ni; if (df[nk] !== -1 || own[nk] !== own[k]) continue;
      df[nk] = df[k] + 1; q.push(nk);
    }
  }
  map.df = df;
}
// take over a fallen neighbour: its land joins yours, its borders become your gates,
// its old heart becomes a second factory, and you salvage what was left
export function annex(p, rid) {
  const R = G.regions[rid], M = G.regions[p.region];
  if (!R.dead || R.mergedInto != null || p.coins < CFG.annexCost) return false;
  const hearts = R.hearts || [R.c];
  p.coins += CFG.salvage * hearts.length - CFG.annexCost;
  const own = G.map.own;
  for (let k = 0; k < own.length; k++) if (own[k] === rid) own[k] = p.region;
  computeDF(G.map);
  M.loops = contourLoops(k => own[k] === p.region); G.mapV++;
  const moved = [];
  for (const s of stationsOf(p).filter(s => s.kind === 'gate' && s.to === rid)) {
    removeStationFromLines(p, s.id);
    moved.push(...s.units);
    G.stations.delete(s.id); p.st = p.st.filter(id => id !== s.id);
  }
  M.nb = M.nb.filter(n => n.to !== rid);
  for (const nb of R.nb) {
    if (nb.to === p.region) continue;
    const other = G.regions[nb.to];
    for (const on of other.nb) if (on.to === rid) on.to = p.region;
    if (other.owner != null && !other.dead) {
      const q = G.players[other.owner];
      for (const s of stationsOf(q)) if (s.kind === 'gate' && s.to === rid) { s.to = p.region; }
      netChanged(q);
    }
    M.nb.push({ ...nb });
    addGate(p, nb);
  }
  R.mergedInto = p.region; R.nb = [];
  // every city that ever stood on this land gives back a factory, so nothing is lost when land changes hands twice
  for (const h of hearts) addStation(p, 'factory', h.x, h.y, { shape: p.shape });
  // shapes from the old shared border are shared out across every factory, and the whole city gets a breather to
  // wire up its new land: an annex must never be what overruns you
  const facs = stationsOf(p).filter(f => f.kind === 'factory');
  moved.forEach((u, i) => facs[i % facs.length].units.push(u));
  for (const st of stationsOf(p)) st.graceUntil = Math.max(st.graceUntil || 0, G.time + CFG.annexGrace);
  M.hearts = [...(M.hearts || [M.c]), ...hearts];
  routesChanged(); p.stats.annexed++; p.rebuild = true;
  netChanged(p);
  emit({ type: 'annex', by: p.id, hearts: hearts.length, salvage: CFG.salvage * hearts.length });
  return true;
}
export function endGame(winner) {
  G.phase = 'over';
  G.winner = winner ? winner.id : null;
  emit({ type: 'end', winner: G.winner });
}

// ---------- bots ----------
export function tour(nodes, start) {
  const rest = nodes.filter(n => n !== start), path = [start];
  let cur = start;
  while (rest.length) {
    let bi = 0, bd = Infinity;
    rest.forEach((n, i) => { const d = dist(cur, n); if (d < bd) { bd = d; bi = i; } });
    cur = rest.splice(bi, 1)[0]; path.push(cur);
  }
  for (let guard = 0, improved = true; improved && guard < 40; guard++) {
    improved = false;
    for (let i = 1; i < path.length - 1; i++) for (let k = i + 1; k < path.length; k++) {
      const a = path[i - 1], b = path[i], c = path[k], d = path[k + 1];
      const before = dist(a, b) + (d ? dist(c, d) : 0), after = dist(a, c) + (d ? dist(b, d) : 0);
      if (after + 0.01 < before) { const seg = path.slice(i, k + 1).reverse(); path.splice(i, k - i + 1, ...seg); improved = true; }
    }
  }
  return path;
}
export function farthestFrom(nodes, pt) { let b = nodes[0], bd = -1; for (const n of nodes) { const d = dist(n, pt); if (d > bd) { bd = d; b = n; } } return b; }
export function rebuildBotLines(p) {
  const all = stationsOf(p);
  const facs = all.filter(s => s.kind === 'factory'), fac = facs[0];
  const gates = all.filter(s => s.kind === 'gate');
  const depots = all.filter(s => s.kind === 'depot' || s.kind === 'recycler');
  const want = [];
  if (gates.length) want.push(tour([...facs, ...gates], fac));
  if (depots.length && gates.length) { const set = [...gates, ...depots]; want.push(tour(set, farthestFrom(set, G.regions[p.region].c))); }
  if (depots.length >= 2) want.push(tour([fac, ...depots], fac));
  while (want.length > p.lineLimit) want.pop();
  want.forEach((w, i) => {
    const ids = w.map(s => s.id);
    if (ids.length < 2) return;
    const L = p.lines[i] ? (setLineStations(p, p.lines[i], ids), p.lines[i]) : createLine(p, ids);
    // close the line into a loop when its ends are about as near each other as its stops are, the way a player
    // would: trains then run both ways round and nothing waits for one to come back down the line
    if (L && ids.length >= 3) {
      let longest = 0; for (let k = 1; k < w.length; k++) longest = Math.max(longest, dist(w[k - 1], w[k]));
      if (dist(w[0], w[w.length - 1]) <= longest * 1.4 && rand() < 0.75) { L.loop = true; netChanged(p); }
    }
  });
  p.rebuild = false;
}
export function depotCost(p) { return CFG.depotBase + CFG.depotStep * p.depots; }
export function upgradeCost(p) { return Math.round(CFG.upgradeBase * (p.rateMax - 1) * Math.pow(1.12, p.rateMax - 2)); }
export function findSpot(p, near) {
  const R = G.regions[p.region];
  for (let i = 0; i < 80; i++) {
    const ang = rand() * Math.PI * 2, rad = 50 + rand() * 170;
    const pt = { x: (near || R.c).x + Math.cos(ang) * rad, y: (near || R.c).y + Math.sin(ang) * rad };
    if (validSpot(p, pt)) return pt;
  }
  return null;
}
export function validSpot(p, pt) {
  const i = Math.floor(pt.x / CELL), j = Math.floor(pt.y / CELL);
  if (i < 0 || j < 0 || i >= GW || j >= GH) return false;
  const k = j * GW + i;
  if (G.map.own[k] !== p.region || G.map.df[k] < 3) return false;
  for (const s of G.stations.values()) if (dist(s, pt) < 52) return false;
  return true;
}
export function buildDepot(p, shape, pt, kind = 'depot') {
  const cost = depotCost(p) + (kind === 'recycler' ? CFG.recyclerExtra : 0);
  if (p.coins < cost) return null;
  p.coins -= cost; p.depots++;
  const s = addStation(p, kind, pt.x, pt.y, { shape });
  netChanged(p);
  return s;
}
export function botThink(p) {
  if (G.time > 1.5 && (p.rebuild || !p.lines.length)) rebuildBotLines(p);
  // depots for whatever is arriving
  const need = {};
  for (const u of unitsIn(p)) { if (u.target === p.id) need[u.shape] = (need[u.shape] || 0) + 1; }
  const depots = stationsOf(p).filter(s => s.kind === 'depot' || s.kind === 'recycler');
  for (const shape in need) {
    const have = depots.filter(d => d.shape === shape).length;
    if ((have === 0 && need[shape] > 0) || (need[shape] > 9 * have && have < 3)) {
      // a second station for a shape is sometimes a recycler, to throw the pressure back
      const kind = have > 0 && rand() < 0.5 ? 'recycler' : 'depot';
      if (p.coins >= depotCost(p) + (kind === 'recycler' ? CFG.recyclerExtra : 0)) {
        const spot = findSpot(p);
        if (spot && buildDepot(p, shape, spot, kind)) { p.rebuild = true; break; }
      }
    }
  }
  if (p.rebuild) rebuildBotLines(p);
  const mc = maxCrowd(p);
  // trains where it hurts
  let worst = null, wr = 0.55;
  for (const s of stationsOf(p)) { const r = s.units.length / capOf(s); if (r > wr) { wr = r; worst = s; } }
  if (worst && (p.coins >= CFG.trainCost + 4 || (p.spare && p.spare.length))) {
    const lines = p.lines.filter(L => L.st.includes(worst.id) && L.trains.length < 2 + Math.floor(L.st.length / 3));
    lines.sort((a, b) => a.trains.length - b.trains.length);
    if (lines[0]) takeTrain(p, lines[0]);
    else {
      const full = p.lines.filter(L => L.st.includes(worst.id) && upgradeable(L).length);
      if (full.length && p.coins >= CFG.carCost * 2 + 4) addCarriage(p, full[Math.floor(rand() * full.length)]);
    }
  }
  // a bigger platform where the crowd keeps building
  if (worst && wr > 0.8 && !worst.warehouse && p.coins >= CFG.warehouseCost + 10) { p.coins -= CFG.warehouseCost; worst.warehouse = true; }
  // middle-men tax the traffic they carry
  if (p.stats.passed > 30 && p.coins >= CFG.customsCost + 30) {
    const g = stationsOf(p).filter(s => s.kind === 'gate' && !s.customs).sort((a, b) => b.units.length - a.units.length)[0];
    if (g) { p.coins -= CFG.customsCost; g.customs = true; }
  }
  // annex fallen neighbours
  const ruin = ruinsNextTo(p)[0];
  if (ruin && p.coins >= CFG.annexCost + LV().slack) annex(p, ruin.id);
  // surges: bank when calm, fire when full
  if (LV().surges && !p.banking && p.bank === 0 && mc < 0.2 && G.time > 120 && rand() < 0.03) p.banking = true;
  if (p.bank >= bankCap(p) * 0.9 || (p.banking && mc > 0.6 && p.bank > 8)) releaseSurge(p);
  if (p.lines.length >= p.lineLimit && p.lineLimit < CFG.lineMax && p.coins >= CFG.lineCost + 25 && depots.length >= 2) {
    p.coins -= CFG.lineCost; p.lineLimit++; p.rebuild = true;
  }
  // production throttle
  if (mc > 0.25 || G.time < 90) p.rate = 1; else p.rate = p.rateMax;
  if (mc < 0.15 && p.coins >= upgradeCost(p) + 10 + LV().slack && p.rateMax < Math.min(CFG.rateCap, LV().rateCap)) { p.coins -= upgradeCost(p); p.rateMax++; }
  // targeting: mostly nearest, sometimes go after the weakest
  p.botTarget -= 1;
  if (p.botTarget <= 0) {
    p.botTarget = 12 + rand() * 10;
    const lv = LV();
    if (rand() < lv.hunt) {
      // go after a city that is actually struggling and not too far away; ties are broken at random, never by seat order
      const c = G.players.filter(q => q.alive && q.id !== p.id && (hops(p.region, q.region) ?? 99) <= lv.huntHops && maxCrowd(q) > lv.huntCrowd)
        .map(q => ({ q, k: maxCrowd(q) + rand() * 0.1 })).sort((a, b) => b.k - a.k);
      p.target = c.length ? c[0].q.id : 'auto';
    } else p.target = 'auto';
  }
}

// ---------- main update ----------
export function update(dt) {
  G.time += dt;
  const dying = new Set();
  for (const p of G.players) if (p.alive) produce(p, dt);
  for (const p of G.players) if (p.alive) for (const L of p.lines) for (const T of L.trains) updateTrain(p, L, T, dt);
  for (let i = G.flights.length - 1; i >= 0; i--) {
    const f = G.flights[i];
    f.t += dt / f.dur;
    if (f.t >= 1) { G.flights.splice(i, 1); landFlight(f); }
  }
  for (const p of G.players) if (p.alive) processStations(p, dt, dying);
  for (const p of G.players) if (p.alive && !p.isHuman) { p.botT -= dt; if (p.botT <= 0) { p.botT = LV().think; botThink(p); } }
  for (const p of dying) eliminate(p);
}

// ---------- track geometry: tracks are smooth curves that flow through stations and bend around them ----------
export const ease = t => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
export function bezPts(a, c1, c2, b, n) {
  const out = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t;
    out.push({ x: u * u * u * a.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t * t * t * b.x, y: u * u * u * a.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t * t * t * b.y });
  }
  return out;
}
// the shared track between two stations, drawn the way a metro map draws it: straight runs at 0, 45 or 90 degrees
// joined by rounded corners. Of the few octilinear shapes that join a and b, it takes the one that stays in the owner's
// land, keeps clear of other stations and best follows the way the lines want to leave each end.
// ta: direction the track leaves a, tb: direction it leaves b (back toward a); null means "no preference"
export function segCurve(a, b, ta, tb, placed) {
  const owner = G.players[a.owner], rid = owner ? owner.region : null;
  const others = [...G.stations.values()].filter(s => s !== a && s !== b && segDist(s, a, b) < dist(a, b) / 2 + 40);
  let best = null, bs = Infinity;
  for (const legs of octoShapes(a, b)) {
    const pts = metroPath(legs, a);
    if (rid != null && !pts.every(q => cellOwner(q) === rid)) continue;
    let sc = (legs.length - 1) * 4;
    for (const st of others) for (const q of pts) { const d = dist(q, st); if (d < 26) sc += (26 - d) * 6; }
    // never run along a different track: two lines on top of each other read as one
    if (placed) for (const q of pts) if (Math.min(dist(q, a), dist(q, b)) > 24 && placed.near(q, 10)) sc += 8;
    const dirOf = (u, v) => { const l = Math.hypot(v.x - u.x, v.y - u.y) || 1; return { x: (v.x - u.x) / l, y: (v.y - u.y) / l }; };
    const d0 = dirOf(legs[0][0], legs[0][1]), d1 = dirOf(legs[legs.length - 1][1], legs[legs.length - 1][0]);
    if (ta) sc += (1 - (ta.x * d0.x + ta.y * d0.y)) * 30;
    if (tb) sc += (1 - (tb.x * d1.x + tb.y * d1.y)) * 30;
    if (sc < bs) { bs = sc; best = pts; }
  }
  if (best) return best;
  // no clean shape fits inside the territory: route through it on the grid, which already moves in the same 8 directions
  return (rid != null && gridRoute(a, b, rid)) || metroPath([[a, b]], a);
}
// the octilinear ways from a to b: one straight run, or a straight run and a 45-degree run in either order,
// or a diagonal between two half straights. Each shape is a list of legs [from, to].
function octoShapes(a, b) {
  const dx = b.x - a.x, dy = b.y - a.y, ax = Math.abs(dx), ay = Math.abs(dy), sx = Math.sign(dx), sy = Math.sign(dy);
  const d = Math.min(ax, ay), straightX = ax > ay;
  if (Math.abs(ax - ay) < 4 || ax < 4 || ay < 4) return [[[a, b]]];
  const diag = { x: sx * d, y: sy * d }, rest = straightX ? { x: sx * (ax - d), y: 0 } : { x: 0, y: sy * (ay - d) };
  const p = (q, v, k = 1) => ({ x: q.x + v.x * k, y: q.y + v.y * k });
  const m1 = p(a, diag), m2 = p(a, rest), h1 = p(a, rest, 0.5), h2 = p(h1, diag);
  return [[[a, m1], [m1, b]], [[a, m2], [m2, b]], [[a, h1], [h1, h2], [h2, b]]];
}
// legs -> a dense polyline with every corner rounded off, so trains glide round it and lanes offset cleanly
export function metroPath(legs, a) {
  const corners = [a, ...legs.map(l => l[1])];
  return roundCorners(corners);
}
export function roundCorners(c) {
  const out = [{ x: c[0].x, y: c[0].y }];
  const step = (u, v) => { const n = Math.max(1, Math.ceil(dist(u, v) / 8)); for (let k = 1; k <= n; k++) out.push({ x: u.x + (v.x - u.x) * k / n, y: u.y + (v.y - u.y) * k / n }); };
  let from = c[0];
  for (let i = 1; i < c.length - 1; i++) {
    const p0 = c[i - 1], p1 = c[i], p2 = c[i + 1], l0 = dist(p0, p1), l1 = dist(p1, p2);
    const r = Math.min(34, l0 * 0.45, l1 * 0.45);
    const e0 = { x: p1.x + (p0.x - p1.x) / (l0 || 1) * r, y: p1.y + (p0.y - p1.y) / (l0 || 1) * r };
    const e1 = { x: p1.x + (p2.x - p1.x) / (l1 || 1) * r, y: p1.y + (p2.y - p1.y) / (l1 || 1) * r };
    step(from, e0);
    // a quadratic through the corner point: a round, even bend from one run into the next
    for (let k = 1; k <= 6; k++) { const t = k / 6, u = 1 - t; out.push({ x: u * u * e0.x + 2 * u * t * p1.x + t * t * e1.x, y: u * u * e0.y + 2 * u * t * p1.y + t * t * e1.y }); }
    from = e1;
  }
  step(from, c[c.length - 1]);
  return out;
}
export function cellOwner(q) {
  const i = Math.floor(q.x / CELL), j = Math.floor(q.y / CELL);
  return i < 0 || j < 0 || i >= GW || j >= GH ? -1 : G.map.own[j * GW + i];
}
// A* across the owner's cells, preferring the interior and steering clear of other stations,
// then thinned and rounded off so it still reads as a flowing line
export function gridRoute(a, b, rid) {
  const own = G.map.own, df = G.map.df, N = GW * GH;
  const cellOf = q => Math.floor(q.y / CELL) * GW + Math.floor(q.x / CELL);
  const s0 = cellOf(a), s1 = cellOf(b);
  if (own[s0] !== rid || own[s1] !== rid) return null;
  const others = [...G.stations.values()].filter(s => s !== a && s !== b);
  const gs = new Float64Array(N).fill(Infinity), from = new Int32Array(N).fill(-1), h = new Heap();
  const hx = k => Math.hypot(k % GW - s1 % GW, Math.floor(k / GW) - Math.floor(s1 / GW));
  gs[s0] = 0; h.push(hx(s0), s0);
  while (h.size) {
    const [, k] = h.pop();
    if (k === s1) break;
    const i = k % GW, j = (k - i) / GW;
    for (const [di, dj, m] of DIRS) {
      const ni = i + di, nj = j + dj; if (ni < 0 || nj < 0 || ni >= GW || nj >= GH) continue;
      const nk = nj * GW + ni; if (own[nk] !== rid) continue;
      const c = cellXY(nk);
      const near = others.some(s => Math.abs(s.x - c.x) < 30 && Math.abs(s.y - c.y) < 30) ? 6 : 0;
      const g = gs[k] + m * (1 + 3 / (1 + df[nk]) + near);
      if (g < gs[nk]) { gs[nk] = g; from[nk] = k; h.push(g + hx(nk), nk); }
    }
  }
  if (from[s1] < 0 && s0 !== s1) return null;
  const cells = [s0]; for (let k = s1; k !== -1 && k !== s0; k = from[k]) cells.splice(1, 0, k);
  // keep only the cells where the grid path turns: those are the corners of an octilinear line
  const corners = [{ x: a.x, y: a.y }];
  for (let i = 1; i + 1 < cells.length; i++) {
    const d0 = cells[i] - cells[i - 1], d1 = cells[i + 1] - cells[i];
    if (d0 !== d1) corners.push(cellXY(cells[i]));
  }
  corners.push({ x: b.x, y: b.y });
  // a one-cell jog in the grid is noise, not a corner
  const clean = [corners[0]];
  for (let i = 1; i < corners.length - 1; i++) if (dist(corners[i], clean[clean.length - 1]) > CELL * 2.5) clean.push(corners[i]);
  clean.push(corners[corners.length - 1]);
  return roundCorners(clean);
}
// shift a track sideways into its lane; lanes run parallel all the way into the station, whose marker covers the ends
export function offsetPath(pts, off) {
  const n = pts.length;
  const out = pts.map((q, i) => {
    const q0 = pts[Math.max(0, i - 1)], q1 = pts[Math.min(n - 1, i + 1)], dx = q1.x - q0.x, dy = q1.y - q0.y, l = Math.hypot(dx, dy) || 1;
    return { x: q.x - dy / l * off, y: q.y + dx / l * off };
  });
  const oc = [0];
  for (let i = 1; i < n; i++) oc.push(oc[i - 1] + dist(out[i - 1], out[i]));
  return { pts: out, cum: oc, len: oc[n - 1] || 1 };
}
// a quick spatial index of track points: is anything already within r of q?
function pointIndex() {
  const C = 12, m = new Map(), k = (i, j) => i * 100003 + j;
  return {
    add(pts) { for (const q of pts) { const c = k(Math.floor(q.x / C), Math.floor(q.y / C)); if (!m.has(c)) m.set(c, []); m.get(c).push(q); } },
    near(q, r) {
      const i0 = Math.floor((q.x - r) / C), i1 = Math.floor((q.x + r) / C), j0 = Math.floor((q.y - r) / C), j1 = Math.floor((q.y + r) / C);
      for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) for (const o of m.get(k(i, j)) || []) if (Math.hypot(o.x - q.x, o.y - q.y) < r) return true;
      return false;
    },
  };
}
export function buildGeometry() {
  G.geoV = G.netV;
  const links = new Map();
  const link = (a, b) => { if (!links.has(a)) links.set(a, new Set()); links.get(a).add(b); };
  const segsOf = L => { const n = L.st.length, out = []; for (let i = 0; i < n - 1 + (L.loop && n > 2 ? 1 : 0); i++) out.push([L.st[i], L.st[(i + 1) % n]]); return out; };
  for (const p of G.players) if (p.alive) for (const L of p.lines) for (const [a, b] of segsOf(L)) { link(a, b); link(b, a); }
  buildLanes();
  // each line's direction at a stop: the smooth average of where it came from and where it goes next.
  // a sharp turn keeps no tangent, so the line meets the station cleanly instead of bulging out.
  const unit = (x, y) => { const l = Math.hypot(x, y) || 1; return { x: x / l, y: y / l }; };
  const tangent = (L, i) => {
    const n = L.st.length, loop = L.loop && n > 2;
    const prev = i > 0 ? L.st[i - 1] : loop ? L.st[n - 1] : null, next = i < n - 1 ? L.st[i + 1] : loop ? L.st[0] : null;
    if (prev == null || next == null) return null;
    const s0 = S(prev), s1 = S(L.st[i]), s2 = S(next);
    const v1 = unit(s1.x - s0.x, s1.y - s0.y), v2 = unit(s2.x - s1.x, s2.y - s1.y);
    if (v1.x * v2.x + v1.y * v2.y < -0.6) return null;   // only a near U-turn stays a sharp corner
    return unit(v1.x + v2.x, v1.y + v2.y);
  };
  // gather, per track (station pair), the leaving directions every line wants, in min->max orientation
  const want = new Map();
  for (const p of G.players) {
    if (!p.alive) continue;
    for (const L of p.lines) {
      const n = L.st.length;
      for (let i = 0; i < n - 1 + (L.loop && n > 2 ? 1 : 0); i++) {
        const j = (i + 1) % n, a = L.st[i], b = L.st[j], ti = tangent(L, i), tj = tangent(L, j);
        // ti points along travel a->b; leaving b back toward a is -tj
        const fromA = ti, fromB = tj && { x: -tj.x, y: -tj.y };
        const key = Math.min(a, b) + '-' + Math.max(a, b), lo = a < b;
        if (!want.has(key)) want.set(key, { a: { x: 0, y: 0, n: 0 }, b: { x: 0, y: 0, n: 0 } });
        const w = want.get(key), add = (acc, t) => { if (t) { acc.x += t.x; acc.y += t.y; acc.n++; } };
        add(lo ? w.a : w.b, fromA); add(lo ? w.b : w.a, fromB);
      }
    }
  }
  const avg = acc => (acc.n && Math.hypot(acc.x, acc.y) > 0.2 ? unit(acc.x, acc.y) : null);
  const curves = new Map();
  for (const p of G.players) {
    if (!p.alive) continue;
    const placed = pointIndex();   // this city's tracks so far, so the next one can keep off them
    for (const L of p.lines) {
      L.geo = segsOf(L).map(([a, b]) => {
        const key = Math.min(a, b) + '-' + Math.max(a, b);
        if (!curves.has(key)) { const w = want.get(key), c = segCurve(S(Math.min(a, b)), S(Math.max(a, b)), avg(w.a), avg(w.b), placed); placed.add(c); curves.set(key, c); }
        const base = curves.get(key);
        return offsetPath(a < b ? base : base.slice().reverse(), laneOff(L, a, b));
      });
    }
  }
}
export function ensureGeom() { if (G.geoV !== G.netV) buildGeometry(); }
// the stretch of track a train uses between two neighbouring stops, and which way it runs along it
export function trainSeg(L, i, j) {
  ensureGeom();
  const n = L.st.length;
  if (j === (i + 1) % n && (L.loop || i + 1 < n)) return { geo: L.geo[i], rev: false };
  return { geo: L.geo[j], rev: true };
}
export function pointAt(geo, f, rev) {
  const s = clamp(f, 0, 1) * geo.len, c = geo.cum, P = geo.pts;
  let i = 0;
  while (i < c.length - 2 && c[i + 1] < s) i++;
  const span = (c[i + 1] - c[i]) || 1, t = clamp((s - c[i]) / span, 0, 1);
  const a = P[i], b = P[i + 1];
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, ang: Math.atan2(b.y - a.y, b.x - a.x) + (rev ? Math.PI : 0) };
}


// ---------- hit tests shared by client and server ----------
export function stationAt(pt, owner) {
  let best = null, bd = 26;
  for (const s of G.stations.values()) { if (owner != null && s.owner !== owner) continue; const d = dist(s, pt); if (d < bd) { bd = d; best = s; } }
  return best;
}

export function snapToLine(p, pt) {
  ensureGeom();
  let best = null, bd = 26;
  for (const L of p.lines) (L.geo || []).forEach((g, i) => {
    g.pts.forEach((q, j) => {
      const f = g.cum[j] / g.len, d = dist(q, pt);
      if (f > 0.12 && f < 0.88 && d < bd) { bd = d; best = { L, i, pos: { x: q.x, y: q.y }, geo: g }; }
    });
  });
  return best;
}

export function insertIntoLine(p, L, i, s) {
  const n = L.st.length, closing = L.loop && i === n - 1;
  const at = closing ? n : i + 1;
  for (const T of L.trains) {
    const onSeg = T.to != null && ((T.idx === i && T.to === (i + 1) % n) || (T.to === i && T.idx === (i + 1) % n));
    if (onSeg) { T.idx = T.t < 0.5 ? T.idx : T.to; T.to = null; T.t = 0; T.wait = 0.1; }
    if (T.idx >= at) T.idx++;
    if (T.to != null && T.to >= at) T.to++;
  }
  L.st.splice(at, 0, s.id);
  netChanged(p);
}

export function lineAt(p, pt) {
  ensureGeom();
  let best = null, bd = 12;
  for (const L of p.lines) for (const g of L.geo || []) for (let j = 0; j + 1 < g.pts.length; j++) {
    const d = segDist(pt, g.pts[j], g.pts[j + 1]);
    if (d < bd) { bd = d; best = L; }
  }
  return best;
}

export function upgradeTarget(p, w, kind) {
  const st = stationAt(w, p.id);
  if (!st) return null;
  if (kind === 'customs') return st.kind === 'gate' && !st.customs ? st : null;
  return !st.warehouse ? st : null;
}
